<template>
    <transition name="slide-in-right">
        <div class="sidebar-container">
            <div class="sidebar-header">
                <h2 class="sidebar-header-title">Exposure Details</h2>
                <button @click="closeDialog" class="p-2 rounded hover:bg-gray-200">
                    <XMarkIcon class="h-6 w-6" />
                </button>
            </div>

            <div class="stepper-container">
                <StatusDisplay v-if="exposure" :exposure="exposure" />
                <div v-else class="stepper-skeleton">
                    <div class="skeleton-bar"></div>
                </div>
            </div>

            <div class="notification-container" v-if="exposure?.status === ExposureStatus.IN_PROGRESS">
                <div class="relative">
                    <div class="circle-1"></div>
                    <div class="circle-2"></div>
                    <InformationCircleIcon class="h-6 w-6 text-amber-400 fill-[white]" />
                </div>
                <p class="notification-text" v-if="company" >
                    We're waiting on {{ company.name }} to respond to our deletion request.
                </p>
            </div>

            <SpecificExposure v-if="exposure" />
            <div v-else class="specific-exposure-skeleton">
                <div class="skeleton-header"></div>
                <div class="skeleton-content">
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line"></div>
                </div>
            </div>

            <ActivityFeed v-if="exposure" />
            <div v-else class="activity-feed-skeleton">
                <div class="skeleton-header"></div>
                <div class="skeleton-content">
                    <div class="skeleton-item"></div>
                    <div class="skeleton-item"></div>
                    <div class="skeleton-item"></div>
                </div>
            </div>
        </div>
    </transition>
</template>
<script setup lang="ts">
import { InformationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import StatusDisplay from '~/components/main/person/dashboard/StatusDisplay.vue';
import ActivityFeed from '~/components/side-bar/person/ActivityFeed.vue';
import SpecificExposure from '~/components/side-bar/person/SpecificExposure.vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import { ModalNames } from '~/types/modals';
import { ExposureStatus } from '~/types/person/details';

const dialogRef = getDialogRef(ModalNames.EXPOSURE_SIDEBAR);
const sidebarStore = useExposureSidebarStore();

const { exposure, company } = storeToRefs(sidebarStore);

const closeDialog = () => {
    dialogRef.value.close();
};
</script>

<style lang="scss" scoped>
.sidebar-container {
    @apply bg-white sm:w-[532px] w-full h-screen flex flex-col overflow-y-auto right-0 fixed top-0;
    animation: slide-in-right 0.3s ease-in-out;

    .sidebar-header {
        @apply flex justify-between items-center w-full p-4;

        .sidebar-header-title {
            @apply text-zinc-900 font-inter text-base ml-4 font-semibold leading-normal;
        }
    }
    .notification-container {
        @apply flex flex-row items-center p-3 px-8 gap-4 border-b border-zinc-200;

        .notification-text {
            @apply text-zinc-700 font-inter text-sm font-semibold ;
        }
    }
}
.stepper-container {
    @apply flex justify-center items-center gap-3 py-2 px-6 bg-zinc-50 border-t border-b border-zinc-200 text-zinc-600 font-inter text-xs font-medium leading-normal;
}
.circle-1 {
    @apply absolute border h-8 w-8 border-amber-400 rounded-full top-[-18%] left-[-18%];
    animation: circle-animation 4s infinite ease-in-out;
}

.circle-2 {
    @apply absolute border h-8 w-8 border-amber-400 rounded-full top-[-18%] left-[-18%];
    animation: circle-animation 6s infinite ease-in-out;
    animation-delay: 1s;
}

@keyframes circle-animation {
    0% {
        border: solid 1px #fff;
        transform: scale(0);
    }

    50% {
        border: solid 1px #f6bb1e;
        transform: scale(1);
    }

    100% {
        border: solid 1px #fff;
        transform: scale(0);
    }
}

//using vue3 transition to slide in from right

@keyframes slide-in-right {
    from {
        transform: translateX(100%);
    }

    to {
        transform: translateX(0);
    }
}

@keyframes slide-out-right {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(100%);
    }
}

// Skeleton loading styles
.stepper-skeleton {
    @apply flex justify-center items-center gap-3 py-2 px-6 bg-zinc-50 border-t border-b border-zinc-200;

    .skeleton-bar {
        @apply h-4 bg-gray-200 rounded animate-pulse;
        width: 200px;
    }
}

.specific-exposure-skeleton {
    @apply p-6 border-b border-zinc-200;

    .skeleton-header {
        @apply h-6 bg-gray-200 rounded animate-pulse mb-4;
        width: 150px;
    }

    .skeleton-content {
        @apply space-y-3;

        .skeleton-line {
            @apply h-4 bg-gray-200 rounded animate-pulse;
            width: 100%;
        }
    }
}

.activity-feed-skeleton {
    @apply p-6;

    .skeleton-header {
        @apply h-6 bg-gray-200 rounded animate-pulse mb-4 w-32;
    }

    .skeleton-content {
        @apply space-y-4;

        .skeleton-item {
            @apply h-16 bg-gray-200 rounded animate-pulse;
            width: 100%;
        }
    }
}
</style>
